import {
  ActionType,
  RoleData,
  AdminData,
  UserData,
  BalanceUpdateValue,
  TicketData,
  OrderData,
  LogValue,
  IAdminLog,
  AdminLogsExportOldValue,
  AdminLogsExportNewValue,
} from 'views/types/adminLogs';
import { adminStore } from 'views/store/useAdminsStore';

const ADMIN_FIELD_MAPPINGS = {
  name: 'Name',
  login: 'Login',
  email: 'Email',
  role: 'Role',
  isActive: 'Active',
  telegramId: 'Telegram ID',
  password: 'Password',
} as const;

const IMPORTANT_ADMIN_FIELDS = Object.keys(ADMIN_FIELD_MAPPINGS) as Array<
  keyof typeof ADMIN_FIELD_MAPPINGS
>;

const formatFieldValue = (key: string, value: unknown): string => {
  if (key === 'password') return '[Changed]';
  if (key === 'isActive') return value ? 'Yes' : 'No';
  return String(value);
};

const formatAdminInfo = (
  admin: AdminData,
  includePassword = false
): string[] => {
  const info: string[] = [];

  if (admin.login) info.push(`Login: ${admin.login}`);
  if (admin.name) info.push(`Name: ${admin.name}`);
  if (admin.role) info.push(`Role: ${admin.role}`);
  if (admin.telegramId) info.push(`Telegram ID: ${admin.telegramId}`);
  if (includePassword && admin.password) info.push(`Password: [Set]`);

  return info;
};

const getAdminLoginById = (adminId?: string): string | undefined => {
  if (!adminId) return undefined;
  try {
    const state = adminStore.getState();
    const found = state.admins.find(a => a.id === adminId);
    return found?.login;
  } catch {
    return undefined;
  }
};

const formatRoleInfo = (role: RoleData): string[] => {
  const info: string[] = [];

  if (role.name) info.push(`Name: ${role.name}`);
  if (role.description) info.push(`Description: ${role.description}`);

  return info;
};

const formatUserInfo = (user: UserData, targetEntityId?: string): string => {
  return user.coinsFlyId
    ? `Coinsfly ID: ${user.coinsFlyId}`
    : `User ID: ${targetEntityId}`;
};

const formatTicketInfo = (
  ticket: TicketData,
  targetEntityId?: string
): string => {
  return ticket.id ? `Ticket ID: ${ticket.id}` : `Ticket ID: ${targetEntityId}`;
};

const formatOrderInfo = (order: OrderData, targetEntityId?: string): string => {
  return order.id ? `Order ID: ${order.id}` : `Order ID: ${targetEntityId}`;
};

export const formatRoleUpdate = (
  newValue: LogValue | undefined,
  targetEntityId?: string
): string => {
  if (!newValue) return `Role ID: ${targetEntityId}`;

  const role = newValue as RoleData;
  const roleName = role.name || 'Unknown Role';
  const details: string[] = [`Role Name: ${roleName}`];

  if (role.permissions) {
    const changedPermissions: string[] = [];
    for (const [resource, perms] of Object.entries(role.permissions)) {
      const permList: string[] = [];
      if (perms.read) permList.push('read');
      if (perms.create) permList.push('create');
      if (perms.update) permList.push('update');
      if (perms.delete) permList.push('delete');

      if (permList.length > 0) {
        changedPermissions.push(`${resource}: ${permList.join(', ')}`);
      }
    }
    if (changedPermissions.length > 0) {
      details.push(`Permissions: ${changedPermissions.join('; ')}`);
    }
  }

  return details.join('\n');
};

const formatAdminUpdate = (
  newValue: LogValue | undefined,
  oldValue: LogValue | undefined,
  _targetEntityId?: string
): string => {
  if (!newValue) return 'No changes';

  const changes: string[] = [];
  const admin = newValue as AdminData;
  const oldAdmin = oldValue as AdminData;

  const resolvedLogin =
    oldAdmin?.login || admin.login || getAdminLoginById(_targetEntityId);
  if (resolvedLogin) changes.push(`Login: ${resolvedLogin}`);

  for (const [key, value] of Object.entries(newValue)) {
    if (
      IMPORTANT_ADMIN_FIELDS.includes(
        key as keyof typeof ADMIN_FIELD_MAPPINGS
      ) &&
      value !== null &&
      key !== 'login'
    ) {
      const fieldName =
        ADMIN_FIELD_MAPPINGS[key as keyof typeof ADMIN_FIELD_MAPPINGS];
      const displayValue = formatFieldValue(key, value);
      changes.push(`${fieldName}: ${displayValue}`);
    }
  }

  return changes.length > 0 ? changes.join(', ') : 'No changes';
};

const formatAdminCreate = (
  newValue: LogValue | undefined,
  targetEntityId?: string
): string => {
  if (!newValue) return `Admin ID: ${targetEntityId}`;

  const admin = newValue as AdminData;
  const adminInfo = formatAdminInfo(admin, true);

  return adminInfo.length > 0
    ? adminInfo.join(', ')
    : `Admin ID: ${targetEntityId}`;
};

const formatAdminDelete = (
  oldValue: LogValue | undefined,
  targetEntityId?: string
): string => {
  if (!oldValue) {
    const login = getAdminLoginById(targetEntityId);
    return login ? `Login: ${login}` : `Admin ID: ${targetEntityId}`;
  }

  const admin = oldValue as AdminData;
  const adminInfo = formatAdminInfo(admin, false);

  if (adminInfo.length > 0) return adminInfo.join(', ');

  const login = getAdminLoginById(targetEntityId);
  return login ? `Login: ${login}` : `Admin ID: ${targetEntityId}`;
};

const formatRoleCreate = (
  newValue: LogValue | undefined,
  targetEntityId?: string
): string => {
  if (!newValue) return `Role ID: ${targetEntityId}`;

  const role = newValue as RoleData;
  const roleInfo = formatRoleInfo(role);

  return roleInfo.length > 0
    ? roleInfo.join(', ')
    : `Role ID: ${targetEntityId}`;
};

const formatRoleDelete = (
  oldValue: LogValue | undefined,
  targetEntityId?: string
): string => {
  if (!oldValue) return `Role ID: ${targetEntityId}`;

  const role = oldValue as RoleData;
  const roleInfo = formatRoleInfo(role);

  return roleInfo.length > 0
    ? roleInfo.join(', ')
    : `Role ID: ${targetEntityId}`;
};

export const formatDetails = (log: IAdminLog): string => {
  const { action_type, old_value, new_value, target_entity_id } = log;

  switch (action_type) {
    case ActionType.ROLE_UPDATE:
      return formatRoleUpdate(new_value, target_entity_id);

    case ActionType.ADMIN_UPDATE:
      return formatAdminUpdate(new_value, old_value, target_entity_id);

    case ActionType.ADMIN_CREATE:
      return formatAdminCreate(new_value, target_entity_id);

    case ActionType.ADMIN_DELETE:
      return formatAdminDelete(old_value, target_entity_id);

    case ActionType.ROLE_CREATE:
      return formatRoleCreate(new_value, target_entity_id);

    case ActionType.ROLE_DELETE:
      return formatRoleDelete(old_value, target_entity_id);

    case ActionType.KYC_APPROVE:
      return new_value
        ? formatUserInfo(new_value as UserData, target_entity_id)
        : `User ID: ${target_entity_id}`;

    case ActionType.KYC_VIEW:
      return new_value
        ? formatUserInfo(new_value as UserData, target_entity_id)
        : `Coinsfly ID: ${target_entity_id}`;

    case ActionType.TICKET_CREATE:
      return new_value
        ? formatTicketInfo(new_value as TicketData, target_entity_id)
        : `Ticket ID: ${target_entity_id}`;

    case ActionType.TICKET_DELETE:
      return old_value
        ? formatTicketInfo(old_value as TicketData, target_entity_id)
        : `Ticket ID: ${target_entity_id}`;

    case ActionType.CHAT_LEAVE: {
      const value = new_value as Record<string, unknown> | undefined;
      const title = value?.title as string | undefined;
      if (title) {
        return title;
      }
      return value?.id
        ? `Chat ID: ${value.id}`
        : `Chat ID: ${target_entity_id}`;
    }

    case ActionType.MESSAGE_SEND: {
      const value = new_value as Record<string, unknown> | undefined;
      const username =
        (value?.username as string) || (value?.userName as string);
      if (username) {
        return `Chat with ${username}`;
      }
      const coinsflyId = value?.userCoinsFlyId as string | undefined;
      if (coinsflyId) {
        return `Chat with ${coinsflyId}`;
      }
      return 'Chat with support chat';
    }

    case ActionType.USER_BLOCK:
    case ActionType.USER_UNBLOCK:
      return new_value
        ? formatUserInfo(new_value as UserData, target_entity_id)
        : `User ID: ${target_entity_id}`;

    case ActionType.USER_DISABLE_2FA:
      return new_value
        ? formatUserInfo(new_value as UserData, target_entity_id)
        : `Coinsfly ID: ${target_entity_id}`;

    case ActionType.USER_BALANCE_UPDATE: {
      const oldV = old_value as BalanceUpdateValue | undefined;
      const newV = new_value as BalanceUpdateValue | undefined;
      const who = newV?.coinsFlyId
        ? `Coinsfly ID: ${newV.coinsFlyId}`
        : `User ID: ${target_entity_id}`;
      const typeSuffix =
        newV?.balanceType === 'blocked' ? ' (blocked)' : ' (available)';
      return `${who}\n${newV?.currency}${typeSuffix}: ${oldV?.amount ?? '0'} → ${newV?.amount}`;
    }

    case ActionType.DEPOSIT_APPROVE:
    case ActionType.DEPOSIT_REJECT:
    case ActionType.WITHDRAW_APPROVE:
    case ActionType.WITHDRAW_REJECT:
      return new_value
        ? formatOrderInfo(new_value as OrderData, target_entity_id)
        : `Order ID: ${target_entity_id}`;

    case ActionType.LOGS_EXPORT_CSV: {
      const exportNewValue = new_value as AdminLogsExportNewValue | undefined;
      const exportOldValue = old_value as AdminLogsExportOldValue | undefined;

      if (log.target_entity_type === 'ADMIN') {
        const details: string[] = [];

        const adminLogin =
          exportNewValue?.targetAdminLogin || exportOldValue?.adminLogin;
        if (adminLogin) {
          details.push(`Login: ${adminLogin}`);
        }

        return details.length > 0
          ? details.join(', ')
          : `Admin ID: ${target_entity_id}`;
      }

      return new_value
        ? formatUserInfo(new_value as UserData, target_entity_id)
        : `User ID: ${target_entity_id}`;
    }

    default:
      return target_entity_id ? `ID: ${target_entity_id}` : '—';
  }
};
